"use client";

import type { WebMcpToolDefinition } from "@/types/webmcp";
import { IMPERATIVE_WEBMCP_TOOL_META } from "@/lib/webmcp/imperative-tool-meta";
import {
  buildExampleReportsPayload,
  buildProductInfoPayload,
} from "@/lib/webmcp/product-catalog";

const [getPricingMeta, getProductInfoMeta, listExampleReportsMeta, navigateMeta] =
  IMPERATIVE_WEBMCP_TOOL_META;

function getModelContext() {
  if (typeof document === "undefined") return null;
  return document.modelContext ?? null;
}

function simplifyPlans(plans: unknown): unknown {
  if (!Array.isArray(plans)) return plans;
  return plans.map((plan) => {
    if (!plan || typeof plan !== "object") return plan;
    const p = plan as Record<string, unknown>;
    const card =
      p.card && typeof p.card === "object"
        ? (p.card as Record<string, unknown>)
        : {};
    return {
      slug: p.slug,
      name: p.name,
      planType: p.planType,
      priceRub: p.priceRub,
      reportCredits: p.reportCredits,
      isSubscription: p.isSubscription,
      purchasableOnline: p.purchasableOnline,
      highlighted: p.highlighted,
      cardTitle: card.cardTitle,
      cardSubtitle: card.cardSubtitle,
      features: Array.isArray(p.features) ? p.features.slice(0, 8) : [],
    };
  });
}

const IMPERATIVE_TOOLS: WebMcpToolDefinition[] = [
  {
    ...getPricingMeta,
    inputSchema: { type: "object", properties: {} },
    annotations: { readOnlyHint: getPricingMeta.readOnlyHint },
    execute: async (_input, { signal }) => {
      const res = await fetch("/api/public/landing-plans", {
        signal,
        cache: "no-store",
      });
      if (!res.ok) {
        throw new Error("Не удалось загрузить тарифы");
      }
      const data = (await res.json()) as { plans?: unknown };
      return JSON.stringify(
        { plans: simplifyPlans(data.plans), source: "/api/public/landing-plans" },
        null,
        2,
      );
    },
  },
  {
    ...getProductInfoMeta,
    inputSchema: { type: "object", properties: {} },
    annotations: { readOnlyHint: getProductInfoMeta.readOnlyHint },
    execute: async () => JSON.stringify(buildProductInfoPayload(), null, 2),
  },
  {
    ...listExampleReportsMeta,
    inputSchema: { type: "object", properties: {} },
    annotations: { readOnlyHint: listExampleReportsMeta.readOnlyHint },
    execute: async () => JSON.stringify(buildExampleReportsPayload(), null, 2),
  },
  {
    ...navigateMeta,
    inputSchema: {
      type: "object",
      properties: {
        topic: {
          type: "string",
          description: "Тема бизнес-идеи для предзаполнения (опционально)",
        },
      },
    },
    annotations: { readOnlyHint: navigateMeta.readOnlyHint },
    execute: async (input, { signal }) => {
      const topic =
        typeof input.topic === "string" ? input.topic.trim().slice(0, 500) : "";
      const createPath = topic
        ? `/reports/create?topic=${encodeURIComponent(topic)}`
        : "/reports/create";

      let loggedIn = false;
      try {
        const sessionRes = await fetch("/api/auth/session", {
          signal,
          cache: "no-store",
        });
        const session = (await sessionRes.json().catch(() => null)) as {
          user?: unknown;
        } | null;
        loggedIn = Boolean(session?.user);
      } catch {
        loggedIn = false;
      }

      if (loggedIn) {
        window.location.assign(createPath);
        return `Открыт мастер создания отчёта: ${createPath}`;
      }

      const loginPath = `/login?callbackUrl=${encodeURIComponent(createPath)}`;
      window.location.assign(loginPath);
      return `Нужен вход. Перенаправление: ${loginPath}`;
    },
  },
];

/** Регистрация imperative WebMCP tools (Chrome origin trial). */
export async function registerIdealitikaWebMcpTools(
  signal?: AbortSignal,
): Promise<number> {
  const ctx = getModelContext();
  if (!ctx?.registerTool) return 0;

  let registered = 0;
  for (const tool of IMPERATIVE_TOOLS) {
    await ctx.registerTool(tool, { signal });
    registered += 1;
  }
  return registered;
}

export { IDEALITIKA_WEBMCP_TOOL_NAMES } from "@/lib/webmcp/imperative-tool-meta";
